import classNames from 'classnames';
import type { Locale } from 'date-fns';
import { format } from 'date-fns/format';
import uniqueId from 'lodash/uniqueId';
import * as React from 'react';
import type { ReactNode } from 'react';
import { useTheme } from '~/Features/Layouts/Theme/Context/ThemeContextProvider';
import { FormLabel } from '~/Partials/FormLabel/FormLabel';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import styles from './BookMeetingCalendarExpander.module.css';

export interface BookMeetingCalendarExpanderTranslations {
    expandCalendar: string;
}

interface BookMeetingCalendarExpanderProps {
    selectedDate: Date | null;
    translations: BookMeetingCalendarExpanderTranslations;
    setAnchorElement: (e: HTMLDivElement | null) => void;
    onClick: () => void;
    label: ReactNode;
    name: string;
    required?: boolean;
    errorMessage?: ReactNode;
    showErrorMessage?: boolean;
    onBlur?: (e: React.FocusEvent) => void;
    locale?: Locale;
}

export const BookMeetingCalendarExpander: React.FC<BookMeetingCalendarExpanderProps> = props => {
    const {
        label,
        name,
        translations,
        setAnchorElement,
        onClick,
        onBlur,
        selectedDate,
        locale,
        errorMessage,
        required = false,
        showErrorMessage = false,
    } = props;

    const theme = useTheme();

    const formatDate = (date: Date) =>
        format(date, 'P', {
            weekStartsOn: 1,
            locale,
        });

    const expandLabelId = uniqueId('expandLabel_');

    return (
        <div
            className={classNames(
                styles.BookMeetingCalendarExpander,
                theme.withRoundedCorners && styles['Variant--rounded'],
                showErrorMessage && styles['Variant--error']
            )}
        >
            <label>
                <span className={styles.Label}>
                    <FormLabel label={label} required={required} />
                </span>
                <div ref={setAnchorElement} className={styles.Inner}>
                    <span className="visuallyHidden" id={expandLabelId}>
                        {translations.expandCalendar}
                    </span>
                    <input
                        name={name}
                        type="button"
                        onClick={onClick}
                        onBlur={onBlur}
                        aria-describedby={expandLabelId}
                        title={translations.expandCalendar}
                        className={styles.Input}
                        required={required}
                    />
                    <div className={styles.Content}>
                        <span>{selectedDate && formatDate(selectedDate)}</span>
                        <SvgIcon className={styles.Icon} iconName="calendar" />
                    </div>
                </div>
                {showErrorMessage && <span className={styles.Error}>{errorMessage}</span>}
            </label>
        </div>
    );
};
